import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Shield, Award, MapPin, Star, ChevronRight, Target, Volume2, Zap, Home as HomeIcon } from 'lucide-react';
import { CATEGORIES, FEATURED_PRODUCTS, TESTIMONIALS } from '../data/products';
import { fetchHomepageSettings, fetchProducts } from '../services/api';
import ProductCard from '../components/ProductCard';

const TRUST_PILLARS = [
  { icon: MapPin, label: 'Made in Texas', sub: 'Lone Star Manufacturing' },
  { icon: Shield, label: 'Battle Tested', sub: 'Hard Use Proven' },
  { icon: Award, label: 'American Made', sub: 'USA Stainless Steel' },
  { icon: Target, label: '60%+ Recoil', sub: 'Reduction Guaranteed' },
  { icon: Zap, label: 'Best Value', sub: 'Premium Without the Price' },
  { icon: Star, label: 'SOT Certified', sub: 'Class III NFA Manufacturer' },
];

const USE_CASES = [
  {
    icon: Target,
    title: 'Tactical & Hard Use',
    desc: 'Our ultra compact suppressors keep your SBR short and maneuverable. Hearing safe on barrels as short as 11.5".',
    img: 'https://wittmachine.net/wp-content/uploads/2022/08/Photo-Jun-08-2-39-17-PM1.jpg',
    href: '/suppressors',
  },
  {
    icon: HomeIcon,
    title: 'Hunting',
    desc: 'Strong enough for any hunting caliber in the backcountry. Protect your ears without adding unreasonable length.',
    img: 'https://wittmachine.net/wp-content/uploads/2022/08/Photo-Jun-24-7-37-57-PM1.jpg',
    href: '/suppressors',
  },
  {
    icon: Volume2,
    title: 'Range & Recreation',
    desc: 'Shoot suppressed and make range day more fun for you and everyone around you. A simple upgrade to your setup.',
    img: 'https://wittmachine.net/wp-content/uploads/2022/08/Photo-May-31-9-20-25-PM1.jpg',
    href: '/shop',
  },
  {
    icon: Shield,
    title: 'Home Defense',
    desc: 'Protect your hearing indoors where no hearing protection is worn. The Naughty 9 and Freakin 45 are purpose-built.',
    img: 'https://wittmachine.net/wp-content/uploads/2025/09/naughty9.webp',
    href: '/suppressors',
  },
];

export default function Home() {
  const [featuredProducts, setFeaturedProducts] = useState(FEATURED_PRODUCTS.slice(0, 6));
  const [heroSettings, setHeroSettings] = useState(null);

  useEffect(() => {
    // Fetch homepage settings from API (hero headline, featured products, banner)
    fetchHomepageSettings().then(settings => {
      if (settings) setHeroSettings(settings);
      if (settings?.featured_products?.length) {
        setFeaturedProducts(settings.featured_products.slice(0, 6));
      } else {
        // Fall back to featured products from API
        fetchProducts().then(prods => {
          const featured = prods.filter(p => p.featured).slice(0, 6);
          if (featured.length) setFeaturedProducts(featured);
        }).catch(() => {});
      }
    }).catch(() => {});
  }, []);

  const heroHeadline = heroSettings?.hero_headline || null;
  const heroSubheadline = heroSettings?.hero_subheadline || null;
  const heroBg = heroSettings?.hero_background_url || 'https://wittmachine.net/wp-content/uploads/2022/08/IMG_04601.jpg';
  const promoBannerText = heroSettings?.promo_banner_text || 'FREE SHIPPING ON ORDERS $450+';
  const promoBannerActive = heroSettings?.promo_banner_active !== false;
  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      {/* ===== HERO ===== */}
      <section
        data-testid="hero-section"
        className="relative min-h-screen flex items-center justify-center overflow-hidden"
        style={{
          backgroundImage: `linear-gradient(135deg, rgba(0,0,0,0.92) 0%, rgba(0,0,0,0.65) 50%, rgba(0,0,0,0.88) 100%), url("${heroBg}")`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed',
        }}
      >
        {/* Subtle orange accent line top */}
        <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-transparent via-orange-500 to-transparent" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 pt-24 pb-20">
          <div className="max-w-3xl">
            {/* Eyebrow */}
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-[2px] bg-orange-500" />
              <span className="font-inter text-xs font-semibold tracking-[0.3em] text-orange-500 uppercase">
                Texas-Made Suppressors & Muzzle Devices
              </span>
            </div>

            {/* Main Headline */}
            <h1
              data-testid="hero-heading"
              className="font-bebas text-6xl sm:text-7xl lg:text-8xl text-white leading-none tracking-wider mb-6 animate-fade-in-up"
            >
              {heroHeadline ? (
                heroHeadline.split('.').filter(Boolean).map((line, i) => (
                  <span key={i} className={`block ${i === 0 || i % 2 === 0 ? '' : 'text-orange-500'}`}>{line.trim()}.</span>
                ))
              ) : (
                <>BUILT FOR<span className="block text-orange-500">BATTLE.</span>PRICED FOR<span className="block text-white">EVERYONE.</span></>
              )}
            </h1>

            {/* Subheadline */}
            <p className="font-inter text-base sm:text-lg text-zinc-300 leading-relaxed mb-10 max-w-xl">
              {heroSubheadline || 'Ultra compact fighting suppressors and precision muzzle devices. American-made in Texas. Battle tested. Unbeatable value.'}
            </p>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/suppressors"
                data-testid="hero-cta-primary"
                className="inline-flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-bebas text-lg tracking-widest px-8 py-4 transition-colors orange-glow"
              >
                SHOP SUPPRESSORS
                <ChevronRight size={18} />
              </Link>
              <Link
                to="/shop"
                data-testid="hero-cta-secondary"
                className="inline-flex items-center justify-center gap-2 border border-white/30 hover:border-white/60 hover:bg-white/5 text-white font-bebas text-lg tracking-widest px-8 py-4 transition-all"
              >
                ALL PRODUCTS
              </Link>
            </div>

            {/* Quick stats */}
            <div className="flex flex-wrap gap-8 mt-12 pt-10 border-t border-white/10">
              {[
                { label: '27,000 sqft', sub: 'Texas Facility' },
                { label: '60%+', sub: 'Recoil Reduction' },
                { label: 'NFA', sub: 'SOT Manufacturer' },
              ].map(stat => (
                <div key={stat.label}>
                  <div className="font-bebas text-2xl text-orange-500 tracking-wider">{stat.label}</div>
                  <div className="font-inter text-xs text-zinc-500 uppercase tracking-widest">{stat.sub}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 opacity-60">
          <div className="w-[1px] h-10 bg-gradient-to-b from-white to-transparent animate-pulse" />
        </div>
      </section>

      {/* ===== PROMO BANNER ===== */}
      {promoBannerActive && (
      <div data-testid="promo-banner" className="bg-orange-500">
        <div className="max-w-7xl mx-auto px-6 py-3 flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-6">
          <span className="font-bebas text-lg text-white tracking-widest">{promoBannerText}</span>
          <span className="hidden sm:block text-white/50 text-xl">|</span>
          <Link to="/shop" className="font-inter text-xs font-bold text-white underline uppercase tracking-widest">
            Shop Now →
          </Link>
        </div>
      </div>
      )}

      {/* ===== TRUST PILLARS ===== */}
      <section data-testid="trust-pillars" className="bg-[#111111] border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-px bg-white/5">
            {TRUST_PILLARS.map((p, i) => {
              const Icon = p.icon;
              return (
                <div key={i} className="flex flex-col sm:flex-row items-center sm:items-center gap-3 p-5 bg-[#111111] text-center sm:text-left">
                  <Icon size={20} className="text-orange-500 flex-shrink-0" />
                  <div>
                    <div className="font-bebas text-base text-white tracking-wider">{p.label}</div>
                    <div className="font-inter text-[11px] text-zinc-500">{p.sub}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ===== CATEGORY GRID ===== */}
      <section data-testid="category-section" className="bg-[#0a0a0a] py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="mb-10">
            <div className="section-divider" />
            <h2 className="font-bebas text-5xl lg:text-6xl text-white tracking-wider mb-3">SHOP BY CATEGORY</h2>
            <p className="font-inter text-sm text-zinc-500">Purpose-built products for serious shooters</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {CATEGORIES.map((cat, i) => (
              <Link
                key={cat.id}
                to={`/${cat.slug}`}
                data-testid={`category-card-${cat.id}`}
                className="relative group overflow-hidden bg-[#1e1e1e] aspect-[3/4] flex flex-col justify-end card-hover-lift"
              >
                {/* Background image */}
                <img
                  src={cat.image}
                  alt={cat.name}
                  className="absolute inset-0 w-full h-full object-cover opacity-40 group-hover:opacity-60 group-hover:scale-105 transition-all duration-500"
                  onError={e => { e.target.style.display = 'none'; }}
                />
                {/* Gradient overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />
                {/* NFA badge */}
                {cat.nfaRequired && (
                  <div className="absolute top-3 right-3 flex items-center gap-1 bg-orange-500/90 px-2 py-1">
                    <Shield size={11} className="text-white" />
                    <span className="font-inter text-[10px] font-bold text-white uppercase">NFA</span>
                  </div>
                )}
                {/* Content */}
                <div className="relative p-5">
                  <div className="w-6 h-[2px] bg-orange-500 mb-2" />
                  <h3 className="font-bebas text-2xl text-white tracking-wider leading-tight">{cat.name}</h3>
                  <p className="font-inter text-xs text-zinc-400 mt-1 mb-3">{cat.tagline}</p>
                  <span className="font-bebas text-sm tracking-widest text-orange-500 group-hover:text-white transition-colors">
                    SHOP NOW →
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ===== FEATURED PRODUCTS ===== */}
      <section data-testid="featured-products" className="bg-[#111111] py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-end justify-between mb-10">
            <div>
              <div className="section-divider" />
              <h2 className="font-bebas text-5xl lg:text-6xl text-white tracking-wider mb-2">FEATURED PRODUCTS</h2>
              <p className="font-inter text-sm text-zinc-500">Our most popular and best-reviewed products</p>
            </div>
            <Link
              to="/shop"
              data-testid="view-all-products-link"
              className="hidden sm:flex items-center gap-2 font-bebas text-sm tracking-widest text-orange-500 hover:text-white transition-colors"
            >
              VIEW ALL <ChevronRight size={16} />
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {featuredProducts.map(product => (
              <ProductCard key={product.id || product._id || product.slug} product={product} />
            ))}
          </div>

          <div className="text-center mt-10">
            <Link
              to="/shop"
              data-testid="shop-all-button"
              className="inline-flex items-center gap-2 font-bebas text-lg tracking-widest bg-orange-500 hover:bg-orange-600 text-white px-8 py-4 transition-colors"
            >
              SHOP ALL PRODUCTS <ChevronRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* ===== ORIGIN STORY ===== */}
      <section data-testid="origin-story" className="bg-[#0a0a0a] py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
            {/* Image */}
            <div className="relative overflow-hidden aspect-[4/3] lg:aspect-auto">
              <img
                src="https://wittmachine.net/wp-content/uploads/2022/08/IMG_04601.jpg"
                alt="Witt Machine Facility"
                className="w-full h-full object-cover"
                onError={e => {
                  e.target.parentElement.style.background = 'linear-gradient(135deg, #1e1e1e, #0a0a0a)';
                  e.target.style.display = 'none';
                }}
              />
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-transparent to-[#0a0a0a] hidden lg:block" />
            </div>

            {/* Text */}
            <div className="bg-[#111111] p-10 lg:p-16 flex flex-col justify-center border border-white/5 border-l-0">
              <div className="section-divider" />
              <h2 className="font-bebas text-4xl lg:text-5xl text-white tracking-wider mb-6">
                FROM 42 INCHES OF CONCRETE
                <span className="block text-orange-500">TO 27,000 SQFT.</span>
              </h2>
              <p className="font-inter text-sm text-zinc-400 leading-relaxed mb-5">
                Ken Wittekiend started Witt Machine & Tool on 42 inches of concrete in an off-grid cabin, making four muzzle brakes a day. Creating new products while constantly reinvesting in better equipment led to a 27,000 sqft manufacturing facility in the great state of Texas.
              </p>
              <p className="font-inter text-sm text-zinc-400 leading-relaxed mb-8">
                From Muzzle Brakes to Fighting Suppressors, Witt Machine & Tool has built a reputation for being durable, effective, and exceeding the investment of the customer. From humble beginnings, Witt Machine & Tool will continue to press the barrier of what is unexpectedly affordable.
              </p>

              {/* Stats grid */}
              <div className="grid grid-cols-3 gap-4 mb-8">
                {[
                  { val: '27K', sub: 'SQ FT Facility' },
                  { val: '60%+', sub: 'Recoil Reduction' },
                  { val: 'TX', sub: 'Made in Texas' },
                ].map(s => (
                  <div key={s.val} className="bg-[#1a1a1a] border border-white/5 p-4 text-center">
                    <div className="font-bebas text-2xl text-orange-500 tracking-wider">{s.val}</div>
                    <div className="font-inter text-[11px] text-zinc-500 uppercase tracking-wider mt-1">{s.sub}</div>
                  </div>
                ))}
              </div>

              <Link
                to="/about"
                data-testid="about-link"
                className="inline-flex items-center gap-2 font-bebas text-sm tracking-widest text-orange-500 hover:text-white transition-colors"
              >
                OUR FULL STORY <ChevronRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ===== USE CASES ===== */}
      <section data-testid="use-cases" className="bg-[#111111] py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <div className="section-divider mx-auto" />
            <h2 className="font-bebas text-5xl lg:text-6xl text-white tracking-wider mb-3">BUILT FOR YOUR APPLICATION</h2>
            <p className="font-inter text-sm text-zinc-500">Whatever your mission, Witt Machine has you covered</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {USE_CASES.map((uc, i) => {
              const Icon = uc.icon;
              return (
                <Link
                  key={i}
                  to={uc.href}
                  data-testid={`use-case-${i}`}
                  className="group relative bg-[#1e1e1e] border border-white/10 hover:border-orange-500/40 overflow-hidden card-hover-lift"
                >
                  <div className="aspect-video overflow-hidden">
                    <img
                      src={uc.img}
                      alt={uc.title}
                      className="w-full h-full object-cover opacity-50 group-hover:opacity-70 group-hover:scale-105 transition-all duration-300"
                      onError={e => {
                        e.target.parentElement.classList.add('bg-[#2a2a2a]');
                        e.target.style.display = 'none';
                      }}
                    />
                  </div>
                  <div className="p-5">
                    <div className="flex items-center gap-2 mb-2">
                      <Icon size={16} className="text-orange-500" />
                      <h3 className="font-bebas text-xl text-white tracking-wide">{uc.title}</h3>
                    </div>
                    <p className="font-inter text-xs text-zinc-500 leading-relaxed">{uc.desc}</p>
                    <div className="mt-4 font-bebas text-sm text-orange-500 group-hover:text-white transition-colors tracking-wider">
                      SHOP NOW →
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* ===== TESTIMONIALS ===== */}
      <section data-testid="testimonials" className="bg-[#0a0a0a] py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <div className="section-divider mx-auto" />
            <h2 className="font-bebas text-5xl lg:text-6xl text-white tracking-wider mb-3">WHAT SHOOTERS SAY</h2>
            <p className="font-inter text-sm text-zinc-500">Real reviews from real customers</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {TESTIMONIALS.slice(0, 3).map(t => (
              <div
                key={t.id}
                data-testid={`testimonial-${t.id}`}
                className="bg-[#1e1e1e] border border-white/10 p-6"
              >
                <div className="flex gap-1 mb-4">
                  {Array.from({ length: t.rating }).map((_, i) => (
                    <Star key={i} size={14} className="text-orange-500 fill-orange-500" />
                  ))}
                </div>
                <p className="font-inter text-sm text-zinc-300 leading-relaxed mb-5 italic">
                  "{t.text}"
                </p>
                <div className="flex items-center justify-between border-t border-white/10 pt-4">
                  <div>
                    <div className="font-bebas text-base text-white tracking-wider">{t.author}</div>
                    <div className="font-inter text-xs text-zinc-600">{t.product}</div>
                  </div>
                  <div className="w-8 h-8 bg-orange-500/20 flex items-center justify-center">
                    <span className="font-bebas text-xs text-orange-500">✓</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== RESOURCES BAR ===== */}
      <section data-testid="resources-section" className="bg-[#111111] border-y border-white/10 py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-10">
            <h2 className="font-bebas text-4xl text-white tracking-wider">RESOURCES & SUPPORT</h2>
            <p className="font-inter text-sm text-zinc-500 mt-1">Everything you need to get the most from your Witt Machine products</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              { title: 'Muzzle Brake FAQ', desc: 'Answers to the most common questions about our muzzle devices and suppressors.', href: '/resources/faq', label: 'Read FAQ' },
              { title: 'Measuring Guide', desc: 'How to measure your barrel for a custom clamp-on muzzle brake that fits perfectly.', href: '/resources/measuring', label: 'View Guide' },
              { title: 'Installation Instructions', desc: 'Step-by-step clamp-on muzzle brake installation. Get it right the first time.', href: '/resources/installation', label: 'Get Instructions' },
            ].map(r => (
              <div key={r.href} className="bg-[#1a1a1a] border border-white/10 p-6 hover:border-orange-500/30 transition-colors group">
                <h3 className="font-bebas text-xl text-white tracking-wide mb-2">{r.title}</h3>
                <p className="font-inter text-sm text-zinc-500 leading-relaxed mb-5">{r.desc}</p>
                <Link
                  to={r.href}
                  data-testid={`resource-link-${r.href.split('/').pop()}`}
                  className="font-bebas text-sm tracking-widest text-orange-500 group-hover:text-white transition-colors"
                >
                  {r.label} →
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== DEALER CTA ===== */}
      <section data-testid="dealer-cta" className="relative bg-[#0a0a0a] py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-orange-500/5 via-transparent to-orange-500/5" />
        <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-orange-500/50 to-transparent" />
        <div className="relative max-w-4xl mx-auto px-6 text-center">
          <div className="section-divider mx-auto" />
          <h2 className="font-bebas text-5xl lg:text-6xl text-white tracking-wider mb-4">
            ARE YOU AN FFL DEALER?
          </h2>
          <p className="font-inter text-base text-zinc-400 leading-relaxed mb-8 max-w-2xl mx-auto">
            Join the Witt Machine dealer program and offer your customers Texas-made suppressors and muzzle devices at wholesale pricing. Apply today to get your dealer account set up.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/dealer-signup"
              data-testid="dealer-cta-button"
              className="inline-flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-bebas text-lg tracking-widest px-8 py-4 transition-colors"
            >
              APPLY FOR DEALER ACCOUNT <ChevronRight size={18} />
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 border border-white/20 hover:border-white/40 text-white font-bebas text-lg tracking-widest px-8 py-4 transition-colors"
            >
              CONTACT US FIRST
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
